import React from "react";
import {
  Badge,
  Box,
  Flex,
  HStack,
  Text,
  VStack,
} from "@chakra-ui/react";
import { DeliveryOption } from "../../types";
import { formatCPTRange } from "../../utils/deliveryTimeUtils";
import { calculateDiscountedPrice } from "../../utils/deliveryCostUtils";
import {
  DeliveryModes,
  CPTSlabForManualOption,
} from "../../../../views/Orders/enums";
import { DeleteOptionControl } from "./DeleteOptionControl";
import {
  DELETE_COLUMN_WIDTH,
  OPTION_ROW_OUTER_GAP,
  getDeliveryOptionCardStyles,
} from "../constants/deliveryOptionRowLayout";

export interface DeliveryOptionRowProps {
  option: DeliveryOption;
  isSelected: boolean;
  isExpired: boolean;
  isRemainingItems: boolean;
  onSelect: () => void;
  onDelete: (optionId: string) => Promise<void>;
  isDeleteDisabled: boolean;
  isDeletingOption: boolean;
}

export const DeliveryOptionRow: React.FC<DeliveryOptionRowProps> = ({
  option,
  isSelected,
  isExpired,
  isRemainingItems,
  onSelect,
  onDelete,
  isDeleteDisabled,
  isDeletingOption,
}) => {
  const showCptSlabBadge =
    option.cptSlab === CPTSlabForManualOption.DIRECT_DELIVERY ||
    option.cptSlab === CPTSlabForManualOption.AGGREGATED_DELIVERY ||
    option.cptSlab === CPTSlabForManualOption.SELF_PICKUP;

  const isInteractionDisabled = isExpired || isRemainingItems;
  const cardStyles = getDeliveryOptionCardStyles({
    isSelected,
    isInteractionDisabled,
  });

  return (
    <HStack spacing={OPTION_ROW_OUTER_GAP} align="center" w="100%">
      <HStack
        flex={1}
        spacing={4}
        {...cardStyles}
        onClick={() => !isInteractionDisabled && onSelect()}
        position="relative"
      >
        <Flex w="100%" align="center">
          <Box w="220px" display="flex" alignItems="center">
            <HStack spacing={2}>
              <Text
                fontSize="0.85rem"
                color="gray.700"
                fontFamily="'Nunito','Inter', sans-serif"
                fontWeight="600"
              >
                {formatCPTRange(option.cptMin, option.cptMax)}
              </Text>
              {option.isSystemGenerated === false && (
                <Badge
                  bg="purple.100"
                  color="purple.700"
                  fontSize="0.65rem"
                  fontWeight="800"
                  px={2}
                  py={0.5}
                  borderRadius="md"
                  textTransform="uppercase"
                >
                  MANUAL
                </Badge>
              )}
              {(option.isSystemGenerated === false ||
                option.cptSlab === CPTSlabForManualOption.SELF_PICKUP ||
                option.deliveryMode === DeliveryModes.SELF_PICKUP) && (
                <Badge
                  bg="orange.100"
                  color="orange.700"
                  fontSize="0.65rem"
                  fontWeight="900"
                  px={2}
                  py={0.5}
                  borderRadius="md"
                  textTransform="uppercase"
                >
                  {option.cptSlab === CPTSlabForManualOption.DIRECT_DELIVERY
                    ? "DD"
                    : option.cptSlab ===
                        CPTSlabForManualOption.AGGREGATED_DELIVERY
                      ? "AD"
                      : "SP"}
                </Badge>
              )}
            </HStack>
          </Box>

          <Box flex="1" display="flex" justifyContent="flex-end">
            <VStack spacing={1} align="flex-end">
              {showCptSlabBadge &&
                option.isSystemGenerated !== false &&
                option.cptSlab !== CPTSlabForManualOption.SELF_PICKUP && (
                  <Badge
                    bg={
                      option.cptSlab === CPTSlabForManualOption.DIRECT_DELIVERY
                        ? "orange.100"
                        : "purple.100"
                    }
                    color={
                      option.cptSlab === CPTSlabForManualOption.DIRECT_DELIVERY
                        ? "orange.700"
                        : "purple.700"
                    }
                    fontSize="0.65rem"
                    fontWeight="900"
                    px={2}
                    py={0.5}
                    borderRadius="md"
                    textTransform="uppercase"
                  >
                    {option.cptSlab === CPTSlabForManualOption.DIRECT_DELIVERY
                      ? "DD"
                      : "AD"}
                  </Badge>
                )}
              {option.deliveryCostDiscountPercentage ? (
                <HStack spacing={2} align="center">
                  <Box position="relative">
                    <Text
                      fontSize="0.8rem"
                      color="gray.500"
                      fontFamily="'Nunito','Inter', sans-serif"
                      fontWeight="600"
                    >
                      ₹{option.deliveryCost}
                    </Text>
                    <Box
                      position="absolute"
                      top="50%"
                      left="-10%"
                      right="-10%"
                      height="1.2px"
                      bg="gray.500"
                      transform="rotate(-12deg)"
                      transformOrigin="center"
                      pointerEvents="none"
                    />
                  </Box>
                  <Text
                    fontSize="0.85rem"
                    color="gray.800"
                    fontFamily="'Nunito','Inter', sans-serif"
                    fontWeight="800"
                    lineHeight="1"
                  >
                    ₹
                    {calculateDiscountedPrice(
                      option.deliveryCost,
                      option.deliveryCostDiscountPercentage,
                    )}
                  </Text>
                </HStack>
              ) : (
                <Text
                  fontSize="0.85rem"
                  color="gray.800"
                  fontFamily="'Nunito','Inter', sans-serif"
                  fontWeight="700"
                  lineHeight="1"
                >
                  ₹ {option.deliveryCost}
                </Text>
              )}
            </VStack>
          </Box>
        </Flex>
      </HStack>
      {!isRemainingItems && (
        <Box
          w={DELETE_COLUMN_WIDTH}
          flexShrink={0}
          display="inline-flex"
          alignItems="center"
          justifyContent="center"
        >
          <DeleteOptionControl
            isDisabled={isDeleteDisabled}
            disabledTooltip="Cannot delete while a change is in progress"
            isDeleting={isDeletingOption}
            onConfirmDelete={() => onDelete(option._id)}
          />
        </Box>
      )}
    </HStack>
  );
};
